import React, { useEffect, useState, useContext, useMemo } from "react";
import axios from "axios";
import { ThemeContext } from "../../context/ThemeContext";
import getCurrentSkin from "../../utils/getCurrentSkin";
import HeaderBar from "../../components/HeaderBar";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaSearch, FaChevronUp, FaChevronDown } from "react-icons/fa";
import PaymentLayoutWrapper from "../../components/PaymentLayoutWrapper";

const BASE_URL = "http://nissi-india.com";

const BillReport = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const [showFilters, setShowFilters] = useState(true);
  const [hasSearched, setHasSearched] = useState(false);
  const [loading, setLoading] = useState(false);

  const [suppliers, setSuppliers] = useState([]);

  const [searchData, setSearchData] = useState({
    fromDate: "",
    toDate: "",
    billNo: "",
    supplier: "",
  });

  const [billData, setBillData] = useState([]);

  // ------------------------------
  // localStorage Helper
  // ------------------------------
  const getLS = (key, fallback = "") => {
    if (typeof window === "undefined") return fallback;
    const val = localStorage.getItem(key);
    return val !== null && val !== undefined ? val : fallback;
  };

  // ✅ localStorage values
  const finYearId = getLS("finYearId");
  const finYear = getLS("finYear");
  const branchId = getLS("branchId") || getLS("branchid");
  const branch = getLS("branchType") || getLS("branchtype") || "HO";
  const userId =
    getLS("userId") || getLS("loginuserid") || getLS("loginUserId");

  // ✅ common headers
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branch,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branch, userId]
  );

  // ------------------------------
  // Helpers
  // ------------------------------
  const datePattern = /^\d{2}\/\d{2}\/\d{4}$/;

  const formatAmount = (amount) => {
    const n = Number(amount || 0);
    return n.toFixed(2);
  };

  const handleInputChange = (field, value) => {
    setSearchData((prev) => ({ ...prev, [field]: value }));
  };

  // ------------------------------
  // Load Suppliers
  // ------------------------------
  const loadSuppliers = async () => {
    try {
      if (!branchId) {
        toast.error("BranchId missing in localStorage!");
        return;
      }

      const res = await axios.get(`${BASE_URL}/api/billReport/suppliers`, {
        params: { branchid: branchId },
        headers: commonHeaders,
      });

      const rows = res.data?.data || [];
      setSuppliers(rows.map((x) => x.suppliername));
    } catch (err) {
      console.error("Suppliers load error:", err);
      toast.error(err.response?.data?.message || "Failed to load suppliers");
    }
  };

  // ------------------------------
  // Set Default Dates
  // ------------------------------
  const setDefaultDates = () => {
    const pad = (n) => String(n).padStart(2, "0");
    const today = new Date();
    const from = new Date();
    from.setDate(today.getDate() - 30);

    const toStr = `${pad(today.getDate())}/${pad(today.getMonth() + 1)}/${today.getFullYear()}`;
    const fromStr = `${pad(from.getDate())}/${pad(from.getMonth() + 1)}/${from.getFullYear()}`;

    setSearchData((prev) => ({
      ...prev,
      fromDate: fromStr,
      toDate: toStr,
    }));
  };

  useEffect(() => {
    setDefaultDates();
  }, []);

  useEffect(() => {
    if (branchId) {
      loadSuppliers();
    }
  }, [branchId, commonHeaders]);

  // ------------------------------
  // Search
  // ------------------------------
  const handleSearch = async () => {
    if (!branchId) {
      toast.error("BranchId missing in localStorage!");
      return;
    }

    if (!searchData.fromDate || !searchData.toDate) {
      toast.error("Please enter both From Date and To Date!");
      return;
    }

    if (!datePattern.test(searchData.fromDate) || !datePattern.test(searchData.toDate)) {
      toast.error("Please enter dates in DD/MM/YYYY format!");
      return;
    }

    try {
      setLoading(true);
      setHasSearched(true);
      toast.info("Searching bill records...");

      const res = await axios.get(`${BASE_URL}/api/billReport/search`, {
        params: {
          branchid: branchId,
          fromDate: searchData.fromDate,
          toDate: searchData.toDate,
          billNo: searchData.billNo,
          supplier: searchData.supplier,
        },
        headers: commonHeaders,
      });

      setBillData(res.data?.data || []);
      toast.success("Search completed.");
    } catch (err) {
      console.error("Search error:", err);
      setBillData([]);
      toast.error(err.response?.data?.message || "Search failed");
    } finally {
      setLoading(false);
    }
  };

  // ------------------------------
  // UI
  // ------------------------------
  return (
    <PaymentLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">BILL REPORT</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex justify-between items-center mb-6">
            <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
              Bill Report
            </h1>

            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${
                isDark
                  ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                  : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
              }`}
            >
              {showFilters ? <FaChevronUp /> : <FaChevronDown />}
              {showFilters ? "Hide Filters" : "Show Filters"}
            </button>
          </div>

          {showFilters && (
            <div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    From Date
                  </label>
                  <input
                    type="text"
                    value={searchData.fromDate}
                    onChange={(e) => handleInputChange("fromDate", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                    }`}
                    placeholder="DD/MM/YYYY"
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Bill No
                  </label>
                  <input
                    type="text"
                    value={searchData.billNo}
                    onChange={(e) => handleInputChange("billNo", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                    }`}
                    placeholder="Enter bill number (prefix)"
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    To Date
                  </label>
                  <input
                    type="text"
                    value={searchData.toDate}
                    onChange={(e) => handleInputChange("toDate", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                    }`}
                    placeholder="DD/MM/YYYY"
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Supplier
                  </label>
                  <select
                    value={searchData.supplier}
                    onChange={(e) => handleInputChange("supplier", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                    }`}
                  >
                    <option value="">Select</option>
                    {suppliers.map((s, index) => (
                      <option key={index} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex justify-center mb-6">
                <button
                  onClick={handleSearch}
                  disabled={loading}
                  className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                    loading ? "opacity-60 cursor-not-allowed" : ""
                  } ${buttonClass} ${hoverClass} text-white`}
                >
                  <FaSearch />
                  {loading ? "Searching..." : "Search"}
                </button>
              </div>
            </div>
          )}

          {hasSearched && (
            <div
              className={`rounded-xl shadow-sm border overflow-hidden ${isDark ? "border-gray-700" : "border-gray-200"}`}
            >
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className={`${buttonClass} text-white`}>
                    <tr>
                      <th className="px-4 py-3 text-center font-semibold border-r border-white/20">S.No</th>
                      <th className="px-4 py-3 text-center font-semibold border-r border-white/20">BillNo</th>
                      <th className="px-4 py-3 text-center font-semibold border-r border-white/20">Date</th>
                      <th className="px-4 py-3 text-center font-semibold border-r border-white/20">Amount</th>
                      <th className="px-4 py-3 text-center font-semibold">Billedby</th>
                    </tr>
                  </thead>

                  <tbody>
                    {billData.length === 0 ? (
                      <tr className={`${isDark ? "bg-gray-800" : "bg-white"}`}>
                        <td
                          colSpan="5"
                          className={`px-4 py-8 text-center text-sm ${isDark ? "text-gray-400" : "text-gray-500"}`}
                        >
                          No Records Found
                        </td>
                      </tr>
                    ) : (
                      billData.map((bill, index) => (
                        <tr
                          key={bill.billid ?? index}
                          className={`transition-colors duration-200 ${
                            isDark ? "bg-gray-800 hover:bg-gray-750" : "bg-white hover:bg-gray-50"
                          }`}
                        >
                          <td className={`px-4 py-3 text-center border-r ${isDark ? "border-gray-700" : "border-gray-200"}`}>
                            {index + 1}
                          </td>
                          <td
                            className={`px-4 py-3 text-center border-r ${
                              isDark ? "border-gray-700 text-blue-400" : "border-gray-200 text-blue-600"
                            }`}
                          >
                            {bill.billNo}
                          </td>
                          <td className={`px-4 py-3 text-center border-r ${isDark ? "border-gray-700" : "border-gray-200"}`}>
                            {bill.date}
                          </td>
                          <td
                            className={`px-4 py-3 text-right border-r font-mono ${
                              isDark ? "border-gray-700" : "border-gray-200"
                            }`}
                          >
                            {formatAmount(bill.amount)}
                          </td>
                          <td className="px-4 py-3 text-center">{bill.billedBy}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </PaymentLayoutWrapper>
  );
};

export default BillReport;